import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  QueryList,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

import { populateModel } from 'utils/utils';
import { Interchange } from '../../models/interchange.model';
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { ProductPanelService } from '../../product-panel.service';
import { segmentsData } from '../../../product-details/segments-data-stub';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { LookupsService } from 'shared/lookups/lookups.service';
import { SearchService } from 'shared/search/search.service';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import {mergeMap} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'interchange-segment',
  templateUrl: 'interchange.component.html',
  styleUrls: ['interchange.component.scss'],
})
export class InterchangeComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;

  headerFields: Array<string> = [
    'brand_label', 'part_number', 'type_code', 'quality_grade_level'
  ];
  textareaFields: Array<string> = [
    'interchange_notes', 'internal_notes'
  ];
  fullSizeFields: Array<string> = [
    'brand_label', 'part_number', 'type_code', 'interchange_notes', 'internal_notes'
  ];
  partNumberData: Array<string> = [
    '99-0504', '1780187401', '99-9999', '46242', '77-9392'
  ];

  declare data: Array<any>;
  dataSources: Array<any> = [];
  declare itemDetailsData: any;
  interchangeFormGroup: UntypedFormGroup;
  loading: boolean = false;
  segmentName: string = 'interchanges';

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
    private lookupsService: LookupsService,
    private fb: UntypedFormBuilder
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  get interchange(): UntypedFormArray {
    return this.interchangeFormGroup.get('interchange') as UntypedFormArray;
  }

  get displayAddNew(): boolean {
    return !this.loading;
  }

  get segmentForm(): UntypedFormGroup {
    return this.interchangeFormGroup;
  }

  ngOnInit() {
    this.interchangeFormGroup = this.fb.group({
      interchange: this.fb.array([])
    });
    let data = segmentsData.interchanges;
    if (this.bulkEdit) {
      data = this.getSegmentsWithPlaceholder(data);
    }
    this.data = this.orderBy(data);
    this.loading = true;
    this.subscribeToItemDetail();
  }

  ngOnDestroy() {
    this.dataSources = [];
    super.ngOnDestroy();
  }

  onAddNewInterchangeClick() {
    const model = populateModel(Interchange, this.data, {});
    this.interchange.push(this.fb.group(model));
    this.buildSegmentDataSources(this.data, model);
    _.delay(() => {
      if (this.tableEditable instanceof TableEditableComponent) {
        this.tableEditable.toggleLastCollapse();
      }
      this.ref.markForCheck();
    }, 100);
  }

  onSelectTypeahead(e: TypeaheadMatch, field_map: any, formGroup: UntypedFormGroup, index: number) {
     formGroup.controls[field_map].setValue(e.item.option);
  }

  onBlurTypeahead(e: any, hiddenField: string, displayField: string, formGroup: UntypedFormGroup) {
    if (!formGroup.controls[hiddenField].value) {
      formGroup.controls[displayField].setValue('');
    }
  }

  onTypeaheadNoResults(e: boolean, field_map: string, formGroup: UntypedFormGroup): void {
    formGroup.controls[field_map].setValue(null);
  }

  // replace parent method:
  setBulkData() {
    this.setData({
      interchanges: this.getBulkSegmentData(this.segmentName)
    });
    this.loading = false;
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.setData(itemDetail);
    this.loading = false;
  }

  setData(itemDetail: any = {}) {
    this.itemDetailsData = itemDetail;
    this.setInterchangeFormArray(this.data, this.itemDetailsData.interchanges);
  }

  setInterchangeFormArray(data: any, itemDetailsData: any) {
    this.dataSources = [];
    const interchangeFGs = _.map(itemDetailsData, (itemDetail: any) => {
      const model = populateModel(Interchange, data, itemDetail);
      this.buildSegmentDataSources(data, model);
      return this.fb.group(model);
    });
    this.interchangeFormGroup.setControl('interchange', this.fb.array(interchangeFGs));
  }

  buildSegmentDataSources(data: any, model: any) {
    _.each(data, (item: any) => {
      if (item.api_lookup) {
        if (!this.dataSources) {
          this.dataSources = [];
        }
        this.dataSources.push(new Observable((observer: any) => {
            // Runs on every search
            observer.next((<any>model)[item.api_lookup.field_map].value);
          })
          .pipe(mergeMap((token: string) => {
            return this.lookupsService.fetch(item.api_lookup.lookup, token);
          })));
        }
    });
  }

  removeRows(rows: any): void {
    const indexes: Array<number> = [];

    _.each(rows, (row: any) => {
      indexes.push(row.index);
    });

    if (indexes.length) {
        _.pullAt(this.dataSources, indexes);
    }
  }

  switchControl(item: any): string {
    if (item.lookup) {
      return 'select';
    } else if (_.includes(this.textareaFields, item.item_name)) {
      return 'textarea';
    } else if (item.item_name === 'brand_AAIAID') {
      return 'typeahead';
    } else {
      return 'input';
    }
  }

  cellSize(itemName: string): string {
    if (_.includes(this.fullSizeFields, itemName)) {
      return 'size-full';
    } else if (itemName === 'language_code') {
      return 'size-quartered';
    } else {
      return 'size-half';
    }
  }

  isHeader(fieldName: string): boolean {
    return (_.indexOf(this.headerFields, fieldName) >= 0);
  }

  save(): Observable<any> {
    if (this.bulkEdit) {
      return this.putDataBulk(
        this.segmentName,
        this.interchange.value,
        this.replaceFieldMapOnItemName(this.interchange.value, segmentsData.interchanges)
      );
    } else {
      const { id } = this.route.snapshot.params;
      return this.putData(id, this.segmentName, this.interchange.value);
    }
  }

}
